import React, { FC, useCallback, useMemo } from 'react';

import { ProviderDto, UserRolesEnum } from '@families-link/shared';
import { useTranslation } from 'react-i18next';
import { CellAction, CoreTableActionsCell } from '../../../_core/components/_ui/core-table';
import { DeleteIcon, EditIcon, EyeIcon } from '../../../_core/components/icons';
import { useCurrentAccessTokenSelector } from '../../../auth';
import { ProvidersTableHandlers } from '.';

export type AllowedProviderActions = 'edit' | 'delete' | 'view';
interface ProvidersTableActionsProps extends ProvidersTableHandlers {
	provider: ProviderDto;
	allowedProviderActions: AllowedProviderActions[];
}

export const ProvidersTableActions: FC<ProvidersTableActionsProps> = (props) => {
	const { provider, allowedProviderActions, onDelete, onEdit, onView } = props;

	const [t] = useTranslation();
	const currentToken = useCurrentAccessTokenSelector();
	const isRequester = currentToken?.payload.sur === UserRolesEnum.Requester;

	const handleDeleteProvider = useCallback(() => {
		if (!provider) {
			return;
		}

		onDelete?.(provider);
	}, [onDelete, provider]);

	const handleEditProvider = useCallback(() => {
		if (!provider) {
			return;
		}

		onEdit?.(provider);
	}, [onEdit, provider]);

	const handleViewProvider = useCallback(() => {
		if (!provider) {
			return;
		}

		onView?.(provider);
	}, [onView, provider]);

	const actions = useMemo<CellAction[]>(
		() => [
			{
				id: 'view',
				type: 'button',
				tooltipTitle: t('buttons.tooltips.full-details'),
				props: {
					children: <EyeIcon sx={{ fontSize: 30 }} color="primary" />,
					onClick: handleViewProvider,
				},
			},
			{
				id: 'more',
				type: 'menu',
				props: {
					options: [
						{
							id: 'edit',
							label: t('providers.edit'),
							icon: <EditIcon color="primary" />,
							onClick: handleEditProvider,
							disabled: isRequester,
						},
						{
							id: 'delete',
							label: t('providers.delete'),
							icon: <DeleteIcon color="primary" />,
							onClick: handleDeleteProvider,
							disabled: isRequester,
						},
					],
				},
			},
		],
		[t, handleViewProvider, handleEditProvider, handleDeleteProvider]
	);

	return <CoreTableActionsCell actions={actions} />;
};
